import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "./UserContext";
import { getSubscription } from "../api/subscriptions";

const EntitlementsContext = createContext({
  subscription: null,
  entitlements: null,
  loading: true,
  error: false,
  refresh: () => {},
});

export const EntitlementsProvider = ({ children }) => {
  const { user } = useContext(UserContext);
  const restaurantId = user?._id;
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  // True when the plan could not be fetched (e.g. backend down) — distinct from
  // "fetched, and the plan doesn't include the feature".
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    if (!restaurantId) {
      setSubscription(null);
      setError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getSubscription(restaurantId);
      setSubscription(data);
      setError(false);
    } catch {
      setSubscription(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      subscription,
      entitlements: subscription?.entitlements || null,
      loading,
      error,
      refresh,
    }),
    [subscription, loading, error, refresh]
  );

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
};

export const useEntitlements = () => useContext(EntitlementsContext);

export const hasFeature = (entitlements, feature) => Boolean(entitlements?.features?.[feature]);
export const getLimit = (entitlements, limitKey) => {
  const value = entitlements?.limits?.[limitKey];
  return value === undefined ? null : value;
};
